/**
 * Exposure ECharts Adapter
 *
 * Transforms exposure data into ECharts pie or stacked bar chart options.
 * - Single resource: Renders as pie/donut chart
 * - Multiple resources: Renders as stacked bar chart for comparison
 */

import type { EChartsCoreOption } from "echarts";
import type {
  ExposureAdapterProps,
  EChartAdapterResult,
  ExposureAPIResponse,
  ExposureResponseItem,
} from "./types";

/**
 * Get display name for a resource
 */
function getDisplayName(
  resourceId: string,
  assetDisplayNames: Record<string, string>
): string {
  const idParts = resourceId.split(":");
  const childId = idParts.length > 1 ? idParts[1] : resourceId;
  return assetDisplayNames[childId] || assetDisplayNames[resourceId] || resourceId;
}

/**
 * Create a diagonal line pattern for chart background
 * Returns a pattern object that can be used as backgroundColor in ECharts
 */
function createPatternBackground(isDarkMode: boolean): object {
  // Create a canvas pattern for diagonal lines
  const patternSize = 10;
  const canvas = document.createElement("canvas");
  canvas.width = patternSize;
  canvas.height = patternSize;
  const ctx = canvas.getContext("2d");
  
  if (ctx) {
    // Set transparent background
    ctx.clearRect(0, 0, patternSize, patternSize);
    
    // Draw diagonal line
    ctx.strokeStyle = isDarkMode ? "rgba(255, 255, 255, 0.03)" : "rgba(0, 0, 0, 0.02)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, patternSize);
    ctx.lineTo(patternSize, 0);
    ctx.stroke();
  }
  
  return {
    image: canvas,
    repeat: "repeat",
  };
}

/**
 * Build pie chart option for single resource
 */
function buildPieOption(
  data: Array<{ name: string; value: number; color?: string }>,
  colorPalette: string[],
  isDarkMode: boolean,
  showTooltips: boolean,
  legendPosition: "right" | "bottom"
): EChartsCoreOption {
  const textColor = isDarkMode ? "#e2e8f0" : "#334155";

  // Apply colors to data items
  const coloredData = data.map((item, index) => ({
    ...item,
    itemStyle: {
      color: item.color || colorPalette[index % colorPalette.length],
    },
  }));

  return {
    backgroundColor: "transparent",
    tooltip: showTooltips
      ? {
          trigger: "item",
          backgroundColor: isDarkMode ? "rgba(30, 41, 59, 0.95)" : "rgba(255, 255, 255, 0.95)",
          borderColor: isDarkMode ? "#475569" : "#e2e8f0",
          textStyle: {
            color: textColor,
          },
          formatter: (params: any) => {
            const percentValue = params.percent || 0;
            const rounded = Math.round(percentValue * 10) / 10;
            const percent = Math.abs(rounded - Math.round(rounded)) < 0.001 
              ? Math.round(rounded).toString() 
              : rounded.toFixed(1);
            return `
              <div style="display: flex; align-items: center; gap: 8px;">
                <span style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background-color: ${params.color};"></span>
                <span>${params.name}:</span>
                <span style="font-weight: 600;">${percent}%</span>
              </div>
            `;
          },
        }
      : undefined,
    legend: {
      show: false, // Use custom legend
    },
    series: [
      {
        type: "pie",
        radius: ["45%", "70%"], // Donut style
        center: legendPosition === "right" ? ["40%", "50%"] : ["50%", "45%"],
        data: coloredData,
        label: {
          show: false,
        },
        labelLine: {
          show: false,
        },
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: "rgba(0, 0, 0, 0.3)",
          },
        },
      },
    ],
    color: colorPalette,
  };
}

/**
 * Build stacked bar chart option for multiple resources
 */
function buildStackedBarOption(
  resourceData: Map<string, Map<string, number>>,
  categories: string[],
  resourceIds: string[],
  assetDisplayNames: Record<string, string>,
  colorPalette: string[],
  isDarkMode: boolean,
  showTooltips: boolean,
  showGridRows: boolean,
  showXAxis: boolean,
  showYAxis: boolean,
  showPatternBackground: boolean
): EChartsCoreOption {
  const textColor = isDarkMode ? "#e2e8f0" : "#334155";
  const gridColor = isDarkMode ? "#475569" : "#e2e8f0";
  const axisLineColor = isDarkMode ? "#64748b" : "#94a3b8";

  // X-axis labels (resource names)
  const xAxisData = resourceIds.map((id) => getDisplayName(id, assetDisplayNames));

  // Build series for each category
  const series = categories.map((category, catIndex) => {
    const data = resourceIds.map((resourceId) => {
      const resourceCategories = resourceData.get(resourceId);
      const value = resourceCategories?.get(category) || 0;
      return (value * 100).toFixed(2); // Convert to percentage
    });

    return {
      name: category,
      type: "bar" as const,
      stack: "total",
      data,
      itemStyle: {
        color:
          category === "Unclassified"
            ? "#E0E0E0"
            : colorPalette[catIndex % colorPalette.length],
      },
      emphasis: {
        focus: "series",
      },
    };
  });

  return {
    backgroundColor: showPatternBackground ? createPatternBackground(isDarkMode) : "transparent",
    tooltip: showTooltips
      ? {
          trigger: "axis",
          axisPointer: {
            type: "shadow",
          },
          backgroundColor: isDarkMode ? "rgba(30, 41, 59, 0.95)" : "rgba(255, 255, 255, 0.95)",
          borderColor: isDarkMode ? "#475569" : "#e2e8f0",
          textStyle: {
            color: textColor,
          },
          formatter: (params: any) => {
            if (!Array.isArray(params) || params.length === 0) return "";

            const resourceName = params[0].axisValue;
            let content = `<div style="font-weight: 600; margin-bottom: 8px;">${resourceName}</div>`;

            params.forEach((param: any) => {
              const value = parseFloat(param.value);
              if (value > 0) {
                const rounded = Math.round(value * 10) / 10;
                const formatted = Math.abs(rounded - Math.round(rounded)) < 0.001 
                  ? Math.round(rounded).toString() 
                  : rounded.toFixed(1);
                content += `
                  <div style="display: flex; align-items: center; gap: 8px; margin: 4px 0;">
                    <span style="display: inline-block; width: 10px; height: 10px; border-radius: 2px; background-color: ${param.color};"></span>
                    <span style="flex: 1;">${param.seriesName}:</span>
                    <span style="font-weight: 600;">${formatted}%</span>
                  </div>
                `;
              }
            });

            return content;
          },
        }
      : undefined,
    legend: {
      show: false, // Use custom legend
    },
    grid: {
      left: showYAxis ? 80 : 10,
      right: 20,
      top: 20,
      bottom: showXAxis ? 60 : 30,
      containLabel: false,
      borderWidth: 0,
    },
    xAxis: {
      type: "category",
      data: xAxisData,
      name: "", // Hide X-axis title - resource names are self-explanatory
      nameLocation: "center",
      nameGap: 40,
      nameTextStyle: {
        color: textColor,
        fontSize: 12,
        fontFamily: "monospace",
      },
      show: true, // Always show for grid lines
      axisLine: {
        show: showXAxis,
        lineStyle: {
          color: axisLineColor,
        },
      },
      axisTick: {
        show: false,
      },
      axisLabel: {
        show: showXAxis,
        color: textColor,
        fontSize: 11,
        fontFamily: "monospace",
        interval: 0,
        rotate: xAxisData.length > 4 ? 30 : 0,
        margin: 10,
        formatter: (value: string) => {
          // Truncate long names
          return value.length > 15 ? value.substring(0, 12) + "..." : value;
        },
      },
    },
    yAxis: {
      type: "value",
      name: showYAxis ? "Allocation (%)" : "",
      nameLocation: "middle",
      nameGap: 50,
      nameTextStyle: {
        color: textColor,
        fontSize: 12,
        fontFamily: "monospace",
      },
      max: 100,
      show: true, // Always show for grid lines
      axisLine: {
        show: showYAxis,
      },
      axisTick: {
        show: false,
      },
      axisLabel: {
        show: showYAxis,
        color: textColor,
        fontSize: 11,
        fontFamily: "monospace",
        formatter: (value: number) => {
          const rounded = Math.round(value * 10) / 10;
          const formatted = Math.abs(rounded - Math.round(rounded)) < 0.001 
            ? Math.round(rounded).toString() 
            : rounded.toFixed(1);
          return `${formatted}%`;
        },
        margin: 15,
      },
      splitLine: {
        show: showGridRows,
        lineStyle: {
          color: gridColor,
          type: "solid",
          opacity: 0.3,
        },
      },
    },
    series,
    color: colorPalette,
  };
}

/**
 * Build ECharts option for exposure charts
 */
export function buildExposureOption(
  props: ExposureAdapterProps
): EChartAdapterResult {
  const {
    rawData,
    selectedAssets,
    assetDisplayNames,
    assetColors,
    colorPalette,
    chartSettings,
    visualSettings,
    isDarkMode,
  } = props;

  const showTooltips = visualSettings.showTooltips ?? true;
  const showGridRows = visualSettings.showGridRows ?? true;
  const showXAxis = visualSettings.showXAxis ?? true;
  const showYAxis = visualSettings.showYAxis ?? true;
  const showPatternBackground = visualSettings.showPatternBackground ?? false;
  const legendPosition = chartSettings.legendPosition || "bottom";
  const showUnclassified = chartSettings.showUnclassified ?? false;

  // Handle empty data
  if (!rawData || rawData.length === 0) {
    return {
      option: {},
      chartType: "pie",
      hasData: false,
    };
  }

  // The first item in rawData is the API response
  const apiResponse = rawData[0] as unknown as ExposureAPIResponse;

  if (!apiResponse) {
    return {
      option: {},
      chartType: "pie",
      hasData: false,
    };
  }

  // Identify resource IDs by checking for objects with 'rows' property
  const resourceIds = Object.keys(apiResponse).filter((key) => {
    const entry = apiResponse[key];
    return (
      key !== "metadata" &&
      !key.startsWith("_") &&
      entry &&
      typeof entry === "object" &&
      "rows" in entry
    );
  });

  if (resourceIds.length === 0) {
    return {
      option: {},
      chartType: "pie",
      hasData: false,
    };
  }

  // Single resource: Pie chart
  if (resourceIds.length === 1) {
    const resourceId = resourceIds[0];
    const resourceData = apiResponse[resourceId] as ExposureResponseItem;

    if (!resourceData?.rows || !Array.isArray(resourceData.rows)) {
      return {
        option: {},
        chartType: "pie",
        hasData: false,
      };
    }

    // Transform rows to pie data
    let pieData = resourceData.rows
      .map((row) => {
        if (!Array.isArray(row) || row.length < 2) return null;
        const name = String(row[0]);
        // Use net value (index 3) if available, otherwise use index 1
        const value = row.length >= 4 ? Number(row[3]) : Number(row[1]);
        if (!name || isNaN(value) || value <= 0) return null;
        return { name, value: value * 100 }; // Convert to percentage
      })
      .filter((item): item is { name: string; value: number } => item !== null)
      .sort((a, b) => b.value - a.value);

    // Optionally add "Unclassified" bucket if total is less than 100%
    // Only enabled for specific exposure types (e.g., asset_allocation) where the universe is the full portfolio
    if (showUnclassified) {
      const total = pieData.reduce((sum, item) => sum + item.value, 0);
      const unclassifiedThreshold = 0.5; // Only show if gap is >= 0.5%
      if (total < 100 - unclassifiedThreshold && total > 0) {
        pieData = [
          ...pieData,
          { name: "Unclassified", value: 100 - total, color: "#E0E0E0" },
        ];
      }
    }

    return {
      option: buildPieOption(pieData, colorPalette, isDarkMode, showTooltips, legendPosition),
      chartType: "pie",
      hasData: pieData.length > 0,
    };
  }

  // Multiple resources: Stacked bar chart
  const allCategories = new Set<string>();
  const resourceDataMap = new Map<string, Map<string, number>>();
  let hasUnclassified = false;

  resourceIds.forEach((resourceId) => {
    const resourceData = apiResponse[resourceId] as ExposureResponseItem;
    if (!resourceData?.rows || !Array.isArray(resourceData.rows)) return;

    const categoryMap = new Map<string, number>();
    let resourceTotal = 0;
    resourceData.rows.forEach((row) => {
      if (!Array.isArray(row) || row.length < 2) return;
      const category = String(row[0]);
      const value = row.length >= 4 ? Number(row[3]) : Number(row[1]);
      if (category && !isNaN(value)) {
        allCategories.add(category);
        categoryMap.set(category, value);
        resourceTotal += value;
      }
    });

    // Optionally add "Unclassified" bucket if total is less than 100%
    // Only enabled for specific exposure types (e.g., asset_allocation) where the universe is the full portfolio
    if (showUnclassified) {
      const unclassifiedThreshold = 0.005; // 0.5% threshold (values are 0-1)
      if (resourceTotal < 1 - unclassifiedThreshold && resourceTotal > 0) {
        categoryMap.set("Unclassified", 1 - resourceTotal);
        hasUnclassified = true;
      }
    }

    resourceDataMap.set(resourceId, categoryMap);
  });

  // Add Unclassified to categories if any resource has it
  if (showUnclassified && hasUnclassified) {
    allCategories.add("Unclassified");
  }

  // Sort categories by typical order
  const categoryOrder = ["Stock", "Bond", "Cash", "Convertible", "Other", "Unclassified"];
  const sortedCategories = Array.from(allCategories).sort((a, b) => {
    const aIndex = categoryOrder.indexOf(a);
    const bIndex = categoryOrder.indexOf(b);
    if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;
    return a.localeCompare(b);
  });

  return {
    option: buildStackedBarOption(
      resourceDataMap,
      sortedCategories,
      resourceIds,
      assetDisplayNames,
      colorPalette,
      isDarkMode,
      showTooltips,
      showGridRows,
      showXAxis,
      showYAxis,
      showPatternBackground
    ),
    chartType: "bar",
    hasData: resourceDataMap.size > 0,
  };
}
